
import { supabase } from "@/lib/supabase";
import { ProfileData } from "@/types/profile";

// Get user profile
export const getProfile = async (userId?: string) => {
  try {
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return null;
      }
      
      userId = session.user.id;
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();  // Changed from .single() to .maybeSingle()
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching profile:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getProfile:', error);
    return null;
  }
};

// Update user profile
export const updateProfile = async (profile: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session found');
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .update(profile)
      .eq('id', session.user.id);
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

// Check if profile has all required fields completed
export const isProfileComplete = async (userId?: string) => {
  try {
    const profile = await getProfile(userId);
    
    if (!profile) return false;
    
    // Check required fields
    return !!(
      profile.full_name && 
      profile.username && 
      profile.bio && 
      profile.interests &&
      profile.avatar_url
    );
  } catch (error) {
    console.error('Error checking profile completion:', error);
    return false;
  }
};

// Create a welcome post for new users
export const createWelcomePost = async (userId: string) => {
  try {
    const { error } = await supabase
      .from('network_posts')
      .insert({
        user_id: userId,
        content: "Hello everyone! I'm new here and excited to join this community!"
      });
    
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error creating welcome post:', error);
    return false;
  }
};

// Helper function to get an empty profile object
export const getEmptyProfile = (): ProfileData => {
  return {
    full_name: '',
    username: '',
    bio: '',
    interests: '',
    grade_level: '',
    school: '',
    avatar_url: ''
  };
};

// Helper to upsert profile data
export const upsertProfile = async (userId: string, profileData: Partial<ProfileData>) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .upsert({
        id: userId,
        ...profileData
      })
      .select()
      .maybeSingle();  // Changed from .single() to .maybeSingle()
    
    if (error) {
      console.error('Error upserting profile:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error upserting profile:', error);
    return null;
  }
};

// Fetch profile by user ID
export const fetchProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();  // Changed from .single() to .maybeSingle()
    
    if (error) {
      if (error.code === 'PGRST116') {
        // Profile not found
        return null;
      }
      console.error('Error fetching profile:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching profile:', error);
    return null;
  }
};
